// src/pages/admin/AdminParameters.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Field, TextInput, PrimaryBtn, Toast, AdminCard, AdminHeading } from '../../components/AdminUI';
import RichTextEditor from '../../components/RichTextEditor';

const RICH_PAGES = [
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'about',   label: 'About' },
  { id: 'terms',   label: 'Terms of Use' },
];

export default function AdminParameters() {
  const [content, setContent] = useState({
    privacy: '', about: '', terms: '', contactEmail: 'axces2000@gmail.com',
    workflowsSlogan: '', nodesSlogan: '', cloudSlogan: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState({ msg: '', type: 'success' });

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  }

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'config', 'parameters'));
        if (snap.exists()) setContent(prev => ({ ...prev, ...snap.data() }));
      } catch (e) {
        showToast('Failed to load parameters.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'parameters'), { ...content, updatedAt: serverTimestamp() });
      showToast('Parameters saved.');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ fontSize: 13, color: 'var(--sand-hint)' }}>Loading…</p>;

  return (
    <div>
      <AdminHeading>Site parameters</AdminHeading>
      <p style={{ fontSize: 12, color: 'var(--sand-muted)', fontStyle: 'italic', marginBottom: 20 }}>
        Footer page content and contact form settings.
      </p>

      <AdminCard>
        <Field label="Contact email" hint="Email address that receives messages from the Contact form.">
          <TextInput
            value={content.contactEmail || ''}
            onChange={v => setContent(prev => ({ ...prev, contactEmail: v }))}
            placeholder="axces2000@gmail.com"
            mono
          />
        </Field>
      </AdminCard>

      <AdminCard>
        <Field label="Workflows tab slogan" hint="Shown under the Workflows tab. Leave blank to use the default.">
          <TextInput
            value={content.workflowsSlogan || ''}
            onChange={v => setContent(prev => ({ ...prev, workflowsSlogan: v }))}
            placeholder="If you have ComfyUI — grab a workflow and start generating."
          />
        </Field>
        <Field label="Nodes tab slogan" hint="Shown under the Nodes tab. Leave blank to use the default.">
          <TextInput
            value={content.nodesSlogan || ''}
            onChange={v => setContent(prev => ({ ...prev, nodesSlogan: v }))}
            placeholder="Enhance ComfyUI with custom nodes."
          />
        </Field>
        <Field label="Cloud tab slogan" hint="Shown under the Cloud tab. Leave blank to use the default.">
          <TextInput
            value={content.cloudSlogan || ''}
            onChange={v => setContent(prev => ({ ...prev, cloudSlogan: v }))}
            placeholder="Run ComfyUI remotely on powerful cloud GPUs — no local install needed."
          />
        </Field>
      </AdminCard>

      {RICH_PAGES.map(p => (
        <AdminCard key={p.id}>
          <Field label={p.label} hint="Supports bold, italic, headings, colour, links and YouTube embeds.">
            <RichTextEditor
              value={content[p.id] || ''}
              onChange={v => setContent(prev => ({ ...prev, [p.id]: v }))}
            />
          </Field>
        </AdminCard>
      ))}

      <PrimaryBtn onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save all changes'}
      </PrimaryBtn>

      <Toast message={toast.msg} type={toast.type} />
    </div>
  );
}
